/**
 * ProgressCard — Expandable inline progress card for agent loop (Spec 167 Phase 3)
 *
 * Replaces the opaque LoadingIndicator spinner with real-time progress steps
 * derived from Glass Box observability events.
 *
 * Collapsed: shows latest step as a single line.
 * Expanded: shows full step log with timestamps.
 * Stop button always visible.
 */

import { useState } from 'react';
import { Loader2, Bot, StopCircle, ChevronDown, ChevronUp, CheckCircle2, Clock } from 'lucide-react';
import type { ProgressStep } from '../../hooks/useProgressSteps';

interface ProgressCardProps {
  steps: ProgressStep[];
  onStop: () => void;
}

function StepIcon({ status }: { status: 'active' | 'completed' }) {
  if (status === 'active') {
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400 shrink-0" />;
  }
  return <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function ProgressCard({ steps, onStop }: ProgressCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const latestStep = steps[steps.length - 1];

  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center shrink-0">
        <Bot className="w-4 h-4 text-text-muted" />
      </div>
      <div className="bg-surface-800 rounded-lg px-3 py-2 min-w-0 flex-1">
        {/* Header: latest step + controls */}
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-text-muted shrink-0" />
          <span className="text-sm text-text-secondary truncate flex-1">
            {latestStep?.label ?? 'Processing...'}
          </span>
          <button
            onClick={() => setIsExpanded(prev => !prev)}
            className="p-1 text-text-muted hover:text-text-secondary hover:bg-surface-600 rounded transition-colors shrink-0"
            title={isExpanded ? 'Collapse progress' : 'Expand progress'}
          >
            {isExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            onClick={onStop}
            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors shrink-0"
            title="Stop agent"
          >
            <StopCircle className="w-4 h-4" />
          </button>
        </div>

        {/* Expanded: full step log */}
        {isExpanded && steps.length > 0 && (
          <div className="mt-2 pt-2 border-t border-surface-600 space-y-1.5 max-h-48 overflow-y-auto">
            {steps.map(step => (
              <div key={step.id} className="flex items-start gap-2 text-xs">
                <StepIcon status={step.status} />
                <span className={
                  step.status === 'active'
                    ? 'text-text-secondary'
                    : 'text-text-muted'
                }>
                  {step.label}
                </span>
                <span className="text-text-muted ml-auto shrink-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(step.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
